import type { CSSProperties, ReactNode } from 'react'

import { useBetSlip, type SlipSelection } from '../lib/betslip.tsx'
import { gbp, odds, pnl, pnlClass } from '../lib/format.ts'
import type { BreachAction, DecisionOutcome, LadderLevel, OutcomeRow, Side } from '../../shared/types.ts'

/**
 * The design system's components, as React.
 *
 * Every class name here comes from `design-system.css` unchanged — this file adds
 * structure and the rules the design makes non-negotiable (colour is never the only
 * carrier of state; liability outweighs stake; a veto reads as a limit, not an error).
 */

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export function Card({
  children,
  pad,
  style,
  className = '',
}: {
  children: ReactNode
  pad?: boolean
  style?: CSSProperties
  className?: string
}) {
  return (
    <div className={`card${pad ? ' pad' : ''}${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </div>
  )
}

export function CardHead({
  title,
  sub,
  right,
  tag,
}: {
  title: ReactNode
  sub?: ReactNode
  right?: ReactNode
  tag?: ReactNode
}) {
  return (
    <div className="card-head">
      {tag}
      <h3 style={tag ? { marginLeft: 8 } : undefined}>{title}</h3>
      {sub && <span className="sub">{sub}</span>}
      <span className="spacer" />
      {right}
    </div>
  )
}

export function CardBody({
  children,
  flush,
  className = '',
  style,
}: {
  children: ReactNode
  flush?: boolean
  className?: string
  style?: CSSProperties
}) {
  return (
    <div className={`card-body${flush ? ' flush' : ''}${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </div>
  )
}

export function CardFoot({ children }: { children: ReactNode }) {
  return <div className="card-foot">{children}</div>
}

// ---------------------------------------------------------------------------
// Stats & meters
// ---------------------------------------------------------------------------

export function Stat({
  label,
  value,
  sub,
  tone,
  large,
  meter,
}: {
  label: ReactNode
  value: ReactNode
  sub?: ReactNode
  tone?: 'pos' | 'neg' | null
  large?: boolean
  meter?: { pct: number; tone?: MeterTone }
}) {
  return (
    <div className="stat">
      <span className="st-label">{label}</span>
      <span className={`st-value${large ? ' lg' : ''}${tone ? ` ${tone}` : ''}`}>{value}</span>
      {meter && (
        <div style={{ marginTop: 8 }}>
          <Meter pct={meter.pct} tone={meter.tone} />
        </div>
      )}
      {sub && <span className="st-sub">{sub}</span>}
    </div>
  )
}

export type MeterTone = 'warn' | 'neg' | 'pos' | undefined

/** Consumption, not headroom: closer to full is closer to a veto. */
export function Meter({ pct, tone, style }: { pct: number; tone?: MeterTone; style?: CSSProperties }) {
  const clamped = Math.max(0, Math.min(100, pct))
  const auto: MeterTone = tone ?? (clamped >= 90 ? 'neg' : clamped >= 75 ? 'warn' : undefined)
  return (
    <div className="meter" style={style} role="meter" aria-valuenow={Math.round(clamped)} aria-valuemin={0} aria-valuemax={100}>
      <div className={`fill${auto ? ` ${auto}` : ''}`} style={{ width: `${clamped}%` }} />
    </div>
  )
}

export function MeterRow({
  label,
  value,
  pct,
  tone,
  last,
}: {
  label: ReactNode
  value: ReactNode
  pct: number
  tone?: MeterTone
  last?: boolean
}) {
  return (
    <div className="meter-row" style={last ? { marginBottom: 0 } : undefined}>
      <span className="mr-label">{label}</span>
      <span className="mr-val">{value}</span>
      <Meter pct={pct} tone={tone} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Badges & severity
// ---------------------------------------------------------------------------

export function Badge({
  tone,
  dot,
  children,
  outline,
}: {
  tone?: string
  dot?: boolean
  outline?: boolean
  children: ReactNode
}) {
  return (
    <span className={`badge${tone ? ` ${tone}` : ''}${outline ? ' outline' : ''}`}>
      {dot && <span className="b-dot" />}
      {children}
    </span>
  )
}

export function SideBadge({ side }: { side: Side }) {
  // Blue backs, pink lays — the exchange convention, and never carried by colour alone.
  return <span className={`badge ${side === 'BACK' ? 'back' : 'lay'}`}>{side}</span>
}

const OUTCOME_CLASS: Record<DecisionOutcome, string> = {
  PLACED: 'placed',
  VETOED: 'vetoed',
  SKIPPED: 'skipped',
  FAILED: 'failed',
}

export function OutcomeBadge({ outcome }: { outcome: DecisionOutcome }) {
  return <span className={`badge ${OUTCOME_CLASS[outcome]}`}>{outcome}</span>
}

const SEV_CLASS: Record<BreachAction, string> = {
  REJECT: 'reject',
  HALT: 'halt',
  FLATTEN_AND_HALT: 'flatten',
}

export function Severity({ action }: { action: BreachAction }) {
  return <span className={`sev ${SEV_CLASS[action]}`}>{action}</span>
}

/** "max liability per market · limit £80.00 · would have been £101.90" — always all three. */
export function VetoLine({ text }: { text: string | null }) {
  if (!text) return null
  return <div className="veto-line">{text}</div>
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

export function Banner({
  tone,
  icon,
  title,
  children,
  actions,
  flush,
}: {
  tone: 'halt' | 'flatten' | 'incident' | 'degraded' | 'success'
  icon?: string
  title: ReactNode
  children?: ReactNode
  actions?: ReactNode
  flush?: boolean
}) {
  const defaultIcon = { halt: '⛔', flatten: '■', incident: '◆', degraded: '◐', success: '●' }[tone]
  return (
    <div className={`banner ${tone}`} style={flush ? { margin: 0 } : undefined}>
      <span className="bn-ico" aria-hidden="true">
        {icon ?? defaultIcon}
      </span>
      <div className="bn-body">
        <div className="bn-title">{title}</div>
        {children && <div className="bn-text">{children}</div>}
      </div>
      {actions && <div className="bn-actions">{actions}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Key/value list
// ---------------------------------------------------------------------------

export function KV({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <dl className="kv" style={style}>
      {children}
    </dl>
  )
}

export function KVRow({ label, children, text }: { label: ReactNode; children: ReactNode; text?: boolean }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={text ? 'text' : undefined}>{children}</dd>
    </>
  )
}

// ---------------------------------------------------------------------------
// Trading widgets
// ---------------------------------------------------------------------------

/**
 * A bookie price. Clicking it puts the selection on the bet slip, or takes it off if it
 * is already there; a selection on the slip shows as picked.
 */
export function OddsButton({
  selection,
  disabled,
}: {
  selection: Omit<SlipSelection, 'price'> & { price: number | null }
  disabled?: boolean
}) {
  const slip = useBetSlip()
  const { price } = selection
  const picked = slip.has(selection.marketId, selection.selectionId)
  return (
    <button
      type="button"
      className={`price-btn odds${picked ? ' picked' : ''}`}
      disabled={disabled || price === null}
      aria-pressed={picked}
      aria-label={`${selection.runner} at ${price === null ? 'no price' : odds(price)}${picked ? ', on your bet slip' : ''}`}
      onClick={() => price !== null && slip.toggle({ ...selection, price })}
    >
      <span className="u-mono">{price === null ? '—' : odds(price)}</span>
    </button>
  )
}

/** Lay prices sit above back prices, best price innermost — the exchange convention. */
export function Ladder({ backs, lays }: { backs: LadderLevel[]; lays: LadderLevel[] }) {
  return (
    <div className="ladder">
      {lays.map((l) => (
        <div className="lad-cell lay" key={`l-${l.price}`}>
          <span>{odds(l.price)}</span>
          <span>{gbp(l.size).replace('.00', '')}</span>
        </div>
      ))}
      {backs.map((b) => (
        <div className="lad-cell back" key={`b-${b.price}`}>
          <span>{odds(b.price)}</span>
          <span>{gbp(b.size).replace('.00', '')}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * The P&L vector — one row per possible result. This is deliberately not summarised into
 * a single number, because it isn't one.
 */
export function OutcomePnl({ outcomes, style }: { outcomes: OutcomeRow[]; style?: CSSProperties }) {
  return (
    <div className="outcome-pnl" style={style}>
      {outcomes.map((o) => (
        <div className="op-row" key={o.selectionId}>
          <span className="op-name">{o.runner} wins</span>
          <span className={`op-val ${pnlClass(o.pnl)}`}>{pnl(o.pnl)}</span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Navigation within a page
// ---------------------------------------------------------------------------

export function Seg<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
  label?: string
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`opt${o.value === value ? ' active' : ''}`}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function ButtonGroup<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
  label?: string
}) {
  return (
    <div className="btn-group" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`btn sm${o.value === value ? ' active' : ''}`}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// States: empty, loading, error
// ---------------------------------------------------------------------------

export function StateBlock({
  icon = '◎',
  title,
  text,
  action,
}: {
  icon?: string
  title: ReactNode
  text?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="state-block">
      <div className="sb-ico" aria-hidden="true">
        {icon}
      </div>
      <div className="sb-title">{title}</div>
      {text && <div className="sb-text">{text}</div>}
      {action}
    </div>
  )
}

export function StateTag({ children, tone }: { children: ReactNode; tone?: 'warn' | 'neg' }) {
  const style =
    tone === 'warn'
      ? { background: 'var(--warn-soft)', color: 'var(--warn)' }
      : tone === 'neg'
        ? { background: 'var(--neg-soft)', color: 'var(--neg)' }
        : undefined
  return (
    <span className="state-tag" style={style}>
      {children}
    </span>
  )
}

export function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="u-sr">Loading</span>
      {Array.from({ length: rows }, (_, i) => (
        <div className="skel skel-line" key={i} style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  )
}

/** The one place a failed fetch is rendered. It never pretends the data is merely empty. */
export function LoadError({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <Card>
      <CardBody>
        <StateBlock
          icon="✕"
          title="Could not load this screen"
          text={
            <>
              {error}
              <br />
              Nothing on this page is being shown from a cache — if the engine is running, its data is simply not
              reaching this screen.
            </>
          }
          action={
            onRetry && (
              <button className="btn outline" onClick={onRetry}>
                Try again
              </button>
            )
          }
        />
      </CardBody>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------

export function PageHead({
  title,
  sub,
  actions,
  breadcrumb,
}: {
  title: ReactNode
  sub?: ReactNode
  actions?: ReactNode
  breadcrumb?: ReactNode
}) {
  return (
    <div className="between page-head">
      <div>
        {breadcrumb && (
          <div className="row" style={{ gap: 8 }}>
            {breadcrumb}
          </div>
        )}
        <h1 style={breadcrumb ? { marginTop: 6 } : undefined}>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  )
}

export function Timeline({ children }: { children: ReactNode }) {
  return <div className="timeline">{children}</div>
}

export function TimelineItem({ time, children }: { time: string; children: ReactNode }) {
  return (
    <div className="tl-item">
      <div className="tl-time">{time}</div>
      <div>{children}</div>
    </div>
  )
}
